import { Resource } from 'constants/resources';

import { useMemo } from 'react';

import { keepPreviousData } from '@tanstack/react-query';
import get from 'lodash/get';
import { RaRecord, useGetList } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Chip } from 'shared/mui/Chips';
import { useAnchor } from 'shared/mui/DatePicker/hooks/useAnchor';
import { useFilterContext } from 'shared/mui/Filter/context/filterContext';
import { FilterChipLabel } from 'shared/mui/Filter/FilterChipsToolbar/FilterChips/components/FilterChipLabel/FilterChipLabel';
import { PopoverChip } from 'shared/mui/Filter/FilterChipsToolbar/FilterChips/components/PopoverChip/PopoverChip';
import { getLimitTagsText } from 'utils/getLimitTagsText';

export type FilterMultiselectChipProps = {
  label: string;
  optionText?: string;
  optionValue?: string;
  renderChipLabel?: (record: any) => string;
  resource: Resource;
  name: string;
  limitTags?: number;
  options?: RaRecord[];
  children: React.ReactNode;
  clearable?: boolean;
  onDelete?: () => void;
  onApply?: () => void;
  onReset?: () => void;
};

export const FilterMultiselectChip: React.FC<FilterMultiselectChipProps> = ({
  resource,
  name,
  label,
  limitTags,
  onApply,
  children,
  onDelete,
  onReset,
  optionText,
  optionValue,
  renderChipLabel,
  options,
  clearable = true,
}) => {
  const { getValues, setValue } = useFormContext();
  const { setAppliedFilter, appliedFilters } = useFilterContext();
  const { handleClose, handleClick, anchorEl } = useAnchor<HTMLDivElement>();

  const filterIds = get(appliedFilters, name) || [];

  const { data = [], isLoading } = useGetList(
    resource,
    {
      filter: { id: filterIds },
      pagination: { page: 1, perPage: filterIds.length || 100 },
      sort: { field: 'id', order: 'ASC' },
    },
    {
      enabled: !options && Boolean(filterIds.length),
      placeholderData: keepPreviousData,
    },
  );

  const tags = useMemo(() => {
    const records = options || data;
    const selected = records.filter((record) => filterIds.includes(record[optionValue || 'id']));
    const names = selected
      .map((record) => {
        if (renderChipLabel) {
          return renderChipLabel(record);
        }
        return record[optionText || 'name'];
      })
      .filter(Boolean);

    return getLimitTagsText(names, limitTags);
  }, [data, options, filterIds, optionText, renderChipLabel, limitTags]);

  if (!filterIds.length) {
    return <></>;
  }

  function onApplyHandler() {
    handleClose();
    onApply && onApply();
    setAppliedFilter(name, getValues(name));
  }

  function onResetHandler() {
    onReset && onReset();
    setAppliedFilter(name, appliedFilters?.[name]);
    setValue(name, appliedFilters?.[name]);
  }
  const open = Boolean(anchorEl);

  return (
    <>
      <PopoverChip
        anchorEl={anchorEl}
        onApply={onApplyHandler}
        onClose={handleClose}
        onReset={onResetHandler}
        open={open}
      >
        {children}
      </PopoverChip>
      <Chip
        label={
          <FilterChipLabel
            isLoading={isLoading || (Boolean(options) && !options?.length)}
            label={label}
            value={tags}
          />
        }
        onClick={handleClick}
        onDelete={
          clearable
            ? () => {
                onDelete && onDelete();
                setValue(name, []);
                setAppliedFilter(name, []);
              }
            : undefined
        }
        size="medium"
      />
    </>
  );
};
